import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { DecisionDialog } from '@/components/portal/decision-dialog';
import { FileDrop } from '@/components/portal/file-drop';
import { FileLink } from '@/components/portal/file-link';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatAmount, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import {
  INBOX_KIND_LABEL,
  INBOX_STATUS_LABEL,
  documentPath,
  errorText,
  isClientRole,
  uploadFile,
  useInbox,
  useOrgRole,
  type InboxFilter,
  type InboxItem,
} from '@/lib/portal';
import { cn } from '@/lib/utils';
import { homeCurrency } from '@/lib/org-locale';
import { useProcessInboxItem } from '@/lib/ai-accountant';

const CHIPS: Array<[InboxFilter, string]> = [
  ['open', 'To review'],
  ['recorded', 'Recorded'],
  ['rejected', 'Not recorded'],
  ['all', 'All'],
];

export const Route = createFileRoute('/documents/inbox')({
  component: Inbox,
  validateSearch: (s: Record<string, unknown>): { status?: InboxFilter } =>
    typeof s['status'] === 'string' && CHIPS.some(([k]) => k === s['status'])
      ? { status: s['status'] as InboxFilter }
      : {},
});

/**
 * Document inbox (document 06, "Client portal"): the client uploads bills and receipts; Outss
 * staff turn each into a draft bill or expense, or say why it cannot be recorded.
 */
function Inbox() {
  const orgId = useOrgId();
  const role = useOrgRole();
  const staff = role !== null && !isClientRole(role);
  const canUpload = staff || role === 'client_owner';
  const { status = staff ? 'open' : 'all' } = Route.useSearch();
  const navigate = useNavigate();
  const list = useInbox(status);
  const qc = useQueryClient();
  const [kind, setKind] = useState<InboxItem['kind']>('bill');
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState<InboxItem | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const processAi = useProcessInboxItem();
  const sendToAi = (i: InboxItem) =>
    processAi.mutate(i.id, {
      onSuccess: (d) => {
        toast(`${i.file.fileName} sent to the AI Accountant.`, {
          link: { to: `/ai-accountant/${d.id}`, label: 'Open' },
        });
      },
      onError: (e) => toast(errorText(e), { tone: 'error' }),
    });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const q = new URLSearchParams({ kind, ...(note.trim() ? { note: note.trim() } : {}) });
      for (const f of files) await uploadFile<InboxItem>(`/orgs/${orgId}/inbox?${q}`, f);
    },
    onSuccess: (_r, files) => {
      toast(
        files.length === 1
          ? `${files[0]!.name} uploaded. Your accountant has been told.`
          : `${files.length} files uploaded. Your accountant has been told.`,
      );
      setNote('');
    },
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['portal', orgId] }),
  });
  const setStatus = useMutation({
    mutationFn: (v: {
      item: InboxItem;
      status: 'received' | 'in_review' | 'rejected';
      reason?: string;
    }) =>
      api<InboxItem>(`/orgs/${orgId}/inbox/${v.item.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: v.status, reason: v.reason, version: v.item.version }),
      }),
    onSuccess: (r) => {
      setRejecting(null);
      setRejectError(null);
      toast(`${r.file.fileName}: ${INBOX_STATUS_LABEL[r.status].toLowerCase()}.`);
    },
    onError: (e) => {
      if (rejecting) setRejectError(errorText(e));
      else toast(errorText(e), { tone: 'error' });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['portal', orgId] }),
  });

  if (!orgId)
    return <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />;
  const rows = list.data ?? [];
  return (
    <>
      <PageHeader
        title="Documents"
        description={
          staff
            ? 'Bills and receipts uploaded by the client and the team. Turn each into a draft bill or expense; the file stays attached.'
            : 'Upload bills and receipts for your accountant. You will see here when each one is recorded.'
        }
      />
      {canUpload ? (
        <div className="mb-5 grid gap-3 rounded-lg border bg-card p-4 md:grid-cols-[220px_1fr]">
          <div className="space-y-3">
            <label className="block space-y-1 text-sm">
              <span className="font-medium">What is it?</span>
              <NativeSelect
                aria-label="Document type"
                value={kind}
                onChange={(e) => setKind(e.target.value as InboxItem['kind'])}
              >
                <option value="bill">Bill to pay</option>
                <option value="receipt">Receipt (already paid)</option>
                <option value="other">Other</option>
              </NativeSelect>
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium">Note for your accountant (optional)</span>
              <Textarea
                aria-label="Note"
                maxLength={1000}
                className="min-h-16"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </div>
          <FileDrop
            disabled={upload.isPending}
            label={upload.isPending ? 'Uploading…' : 'Drag and drop bills and receipts here, or'}
            onFiles={(f) => upload.mutate(f)}
            onProblem={(m) => toast(m, { tone: 'error' })}
          />
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Status">
        {CHIPS.map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={status === k}
            className={cn(
              'rounded-full border px-3 py-1 text-sm',
              status === k ? 'border-primary bg-primary/10 text-primary' : 'bg-card',
            )}
            onClick={() => void navigate({ to: '/documents/inbox', search: { status: k } })}
          >
            {label}
          </button>
        ))}
      </div>

      {list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.isError ? (
        <EmptyState title="Could not load the documents" hint={list.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title={status === 'open' ? 'Nothing to review' : 'No documents here'}
          hint={
            canUpload
              ? 'Uploaded bills and receipts appear here.'
              : 'Client owners upload bills and receipts here.'
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Uploaded</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="w-[90px]">Type</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="w-[130px]">Uploaded by</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[200px]">Recorded as</TableHead>
                {staff ? <TableHead className="w-[150px] text-right">Action</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((i) => (
                <TableRow key={i.id} data-testid="inbox-row">
                  <TableCell className="tnum">{formatDateTime(i.uploadedAt)}</TableCell>
                  <TableCell className="max-w-[280px] whitespace-normal">
                    <FileLink file={i.file} />
                  </TableCell>
                  <TableCell>{INBOX_KIND_LABEL[i.kind]}</TableCell>
                  <TableCell className="max-w-[280px] whitespace-normal text-sm">
                    {i.note}
                    {i.status === 'rejected' && i.rejectReason ? (
                      <div className="text-destructive">Not recorded: {i.rejectReason}</div>
                    ) : null}
                  </TableCell>
                  <TableCell>{i.uploadedBy}</TableCell>
                  <TableCell>
                    <InboxChip status={i.status} />
                  </TableCell>
                  <TableCell className="whitespace-normal text-sm">
                    {staff && i.aiDocument && !i.document ? (
                      <Link
                        className="text-primary hover:underline"
                        to="/ai-accountant/$id"
                        params={{ id: i.aiDocument.id }}
                      >
                        In AI Accountant
                      </Link>
                    ) : null}
                    {i.document ? (
                      <Link
                        className="text-primary hover:underline"
                        to={documentPath(i.document.kind, i.document.id)}
                      >
                        {i.document.kind === 'expense' ? 'Expense' : 'Bill'}{' '}
                        {i.document.number ?? '(draft)'} · {i.document.contactName}
                        <span className="tnum">
                          {' '}
                          · {homeCurrency()} {formatAmount(i.document.total)}
                        </span>
                      </Link>
                    ) : null}
                  </TableCell>
                  {staff ? (
                    <TableCell className="text-right">
                      {i.status === 'recorded' ? null : (
                        <RowActions
                          item={i}
                          onCreate={(k) =>
                            void navigate({
                              to: k === 'bill' ? '/expenses/bills/new' : '/expenses/expenses/new',
                              search: { fromInbox: i.id },
                            })
                          }
                          onStatus={(s) => setStatus.mutate({ item: i, status: s })}
                          onReject={() => setRejecting(i)}
                          onAi={() =>
                            i.aiDocument
                              ? void navigate({
                                  to: '/ai-accountant/$id',
                                  params: { id: i.aiDocument.id },
                                })
                              : sendToAi(i)
                          }
                        />
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <DecisionDialog
        open={Boolean(rejecting)}
        title={`Mark ${rejecting?.file.fileName ?? 'this upload'} as not recordable?`}
        description="The person who uploaded it is told why. You can move it back to review later."
        confirm="Reject"
        required
        label="Reason"
        pending={setStatus.isPending}
        error={rejectError}
        onConfirm={(reason) =>
          rejecting && setStatus.mutate({ item: rejecting, status: 'rejected', reason })
        }
        onClose={() => {
          setRejecting(null);
          setRejectError(null);
        }}
      />
    </>
  );
}

function InboxChip({ status }: { status: InboxItem['status'] }) {
  return (
    <span
      data-testid="inbox-status"
      className={cn(
        'inline-flex rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        status === 'recorded'
          ? 'border-success/30 bg-success/10 text-success'
          : status === 'rejected'
            ? 'border-destructive/30 bg-destructive/10 text-destructive'
            : status === 'in_review'
              ? 'border-warning/30 bg-warning/10 text-warning'
              : 'border-info/30 bg-info/10 text-info',
      )}
    >
      {INBOX_STATUS_LABEL[status]}
    </span>
  );
}

function RowActions({
  item,
  onCreate,
  onStatus,
  onReject,
  onAi,
}: {
  item: InboxItem;
  onCreate: (kind: 'bill' | 'expense') => void;
  onStatus: (s: 'received' | 'in_review') => void;
  onReject: () => void;
  /** Process with AI Accountant (staff), or open it there. */
  onAi: () => void;
}) {
  const primary: 'bill' | 'expense' = item.kind === 'receipt' ? 'expense' : 'bill';
  return (
    <div className="inline-flex">
      <Button size="sm" className="rounded-r-none" onClick={() => onCreate(primary)}>
        {primary === 'bill' ? 'Create bill' : 'Create expense'}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            className="rounded-l-none border-l border-primary-foreground/30 px-2"
            aria-label="More actions"
          >
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onCreate(primary === 'bill' ? 'expense' : 'bill')}>
            {primary === 'bill' ? 'Create expense' : 'Create bill'}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onAi}>
            {item.aiDocument ? 'Open in AI Accountant' : 'Process with AI Accountant'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {item.status !== 'in_review' ? (
            <DropdownMenuItem onSelect={() => onStatus('in_review')}>
              Mark in review
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => onStatus('received')}>
              Back to received
            </DropdownMenuItem>
          )}
          {item.status !== 'rejected' ? (
            <DropdownMenuItem onSelect={onReject}>Cannot be recorded…</DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
